import React from 'react';
import FormInput from '../FormInput/FormInput';
import styles from './PortalUserProfileForm.module.css';

export type PortalUserCredentialsVariant = 'account' | 'admin-create' | 'admin-edit';

export interface PortalUserCredentialsValues {
  fullName: string;
  email: string;
  currentPassword: string;
  password: string;
  confirmPassword: string;
}

export type PortalUserCredentialsField = keyof PortalUserCredentialsValues;

export interface PortalUserCredentialsErrors {
  fullName?: string;
  email?: string;
  currentPassword?: string;
  password?: string;
  confirmPassword?: string;
}

interface PortalUserProfileCredentialsSectionProps {
  idPrefix: string;
  variant: PortalUserCredentialsVariant;
  values: PortalUserCredentialsValues;
  errors: PortalUserCredentialsErrors;
  disabled?: boolean;
  onChange: (field: PortalUserCredentialsField, value: string) => void;
}

const PASSWORD_HINTS: Record<PortalUserCredentialsVariant, string> = {
  account: 'Para alterar a senha, informe a senha atual e a nova senha. Deixe todos os campos em branco para mantê-la.',
  'admin-create': 'Defina a senha inicial de acesso da usuária.',
  'admin-edit': 'Você pode alterar só o e-mail sem informar senha. Deixe a senha em branco para mantê-la.',
};

const PortalUserProfileCredentialsSection: React.FC<PortalUserProfileCredentialsSectionProps> = ({
  idPrefix,
  variant,
  values,
  errors,
  disabled = false,
  onChange,
}) => {
  const handleFieldChange = (field: PortalUserCredentialsField) => (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    onChange(field, event.target.value);
  };

  const isAdminCreate = variant === 'admin-create';
  const isAdminEdit = variant === 'admin-edit';
  const showCurrentPassword = variant === 'account';
  const passwordRequired = isAdminCreate;
  const passwordLabel = isAdminEdit ? 'Nova senha (opcional)' : (isAdminCreate ? 'Senha' : 'Nova senha');
  const confirmPasswordLabel = isAdminEdit ? 'Confirmar nova senha (opcional)' : (isAdminCreate ? 'Confirmar senha' : 'Confirmar nova senha');
  const passwordPlaceholder = isAdminEdit
    ? 'Deixe em branco para manter a senha atual'
    : (isAdminCreate ? 'Digite a senha' : 'Digite a nova senha');
  const confirmPasswordPlaceholder = isAdminEdit
    ? 'Repita a nova senha para confirmar'
    : (isAdminCreate ? 'Repita a senha' : 'Repita a nova senha');

  return (
    <>
      <h3 className={styles.sectionTitle}>Informações da conta</h3>

      <div className={styles.formFields}>
        <FormInput
          id={`${idPrefix}-full-name`}
          label="Nome completo"
          required
          value={values.fullName}
          onChange={handleFieldChange('fullName')}
          error={errors.fullName}
          disabled={disabled}
          placeholder="Digite o nome completo"
        />

        <FormInput
          id={`${idPrefix}-email`}
          label="E-mail"
          type="email"
          required
          value={values.email}
          onChange={handleFieldChange('email')}
          error={errors.email}
          disabled={disabled}
          placeholder="exemplo@dominio.com"
        />

        <div>
          <h3 className={styles.sectionTitle}>
            {isAdminCreate ? 'Senha de acesso' : 'Alterar senha'}
          </h3>
          <p className={styles.passwordHint}>{PASSWORD_HINTS[variant]}</p>
        </div>

        {showCurrentPassword && (
          <FormInput
            id={`${idPrefix}-current-password`}
            label="Senha atual"
            type="password"
            value={values.currentPassword}
            onChange={handleFieldChange('currentPassword')}
            error={errors.currentPassword}
            disabled={disabled}
            placeholder="Digite a senha atual"
            autoComplete="current-password"
          />
        )}

        <FormInput
          id={`${idPrefix}-password`}
          label={passwordLabel}
          type="password"
          required={passwordRequired}
          value={values.password}
          onChange={handleFieldChange('password')}
          error={errors.password}
          disabled={disabled}
          placeholder={passwordPlaceholder}
          autoComplete="new-password"
        />

        <FormInput
          id={`${idPrefix}-confirm-password`}
          label={confirmPasswordLabel}
          type="password"
          required={passwordRequired}
          value={values.confirmPassword}
          onChange={handleFieldChange('confirmPassword')}
          error={errors.confirmPassword}
          disabled={disabled}
          placeholder={confirmPasswordPlaceholder}
          autoComplete="new-password"
        />
      </div>
    </>
  );
};

export default PortalUserProfileCredentialsSection;
